import { ColorModel, MaterialModel, MaterialsAPIItem } from '@common/models';
import { useFetchMaterialsQuery } from '@ee-api/materials-api/materialsService';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { MaterialsQueryParams } from '@ee-api/queryParams';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useStores } from '@ee-api/stores-api/useStores';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { sortColors } from '../../../pages/easy-estimator/components/scrollable-area/colors/colors.utils';
import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';

type useColorsReturn = TypedUseQueryHookResult<MaterialsAPIItem[], MaterialsQueryParams, AxiosBaseQueryFunction> & {
    colors: ColorModel[];
    isFetchingColors: boolean;
};

const emptyArray = [] satisfies ColorModel[] | MaterialModel[];

/**
 * select the list of colors based on the currently selected material and sort them
 */
const selectColorsFromMaterial = createSelector(
    [(data?: MaterialsAPIItem[]) => data, (_, selectedMaterial) => selectedMaterial, (_, _selected, sortBy) => sortBy],
    (_apiHookResponse, selectedMaterial, sortBy) => {
        if (!selectedMaterial) {
            return emptyArray;
        }
        return sortColors(selectedMaterial.colors, sortBy);
    }
);

const useColors = (): useColorsReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { isFetchingStores } = useStores();
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    // This uses the Materials query, but gets the colors based on the selectedMaterial
    const { colors, ...rest } = useFetchMaterialsQuery(
        !isFetchingStores && zipcode && selectedStore?.companyId
            ? {
                  params: {
                      zipCode: zipcode,
                      storeId: selectedStore.companyId,
                  },
              }
            : skipToken,
        {
            selectFromResult: (result) => {
                return {
                    colors: result.isError ? emptyArray : selectColorsFromMaterial(result.data, selectedMaterial, null),
                    ...result,
                };
            },
        }
    );

    return {
        colors,
        isFetchingColors:
            rest.isFetching ||
            (!!zipcode &&
                !!selectedStore &&
                (zipcode !== rest.originalArgs?.params?.zipCode ||
                    selectedStore.companyId !== rest.originalArgs?.params?.storeId)),
        ...rest,
    };
};

export default useColors;
